/*
 *   Copyright 2022-2024 SenX S.A.S.
 *
 *   Licensed under the Apache License, Version 2.0 (the "License");
 *   you may not use this file except in compliance with the License.
 *   You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 *   Unless required by applicable law or agreed to in writing, software
 *   distributed under the License is distributed on an "AS IS" BASIS,
 *   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *   See the License for the specific language governing permissions and
 *   limitations under the License.
 */
import { h, Host } from "@stencil/core";
import { Utils } from "../../utils/utils";
import { Param } from "../../model/param";
import { Logger } from "../../utils/logger";
import { GTSLib } from "../../utils/gts.lib";
import { Dashboard } from "../../model/types";
import { JsonLib } from "../../utils/jsonLib";
import { v4, v4 as uuidv4 } from "uuid";
import { PdfLib } from "../../utils/pdfLib";
import { LangUtils } from "../../utils/lang-utils";
import { isEqual } from "lodash";
export class DiscoveryDashboardComponent {
    constructor() {
        this.done = {};
        this.innerVars = {};
        this.eventState = {};
        this.url = undefined;
        this.dashboardTitle = undefined;
        this.data = undefined;
        this.warpscript = undefined;
        this.options = new Param();
        this.debug = false;
        this.autoRefresh = -1;
        this.cellHeight = 220;
        this.cols = 12;
        this.type = 'dashboard';
        this.vars = '{}';
        this.width = undefined;
        this.height = undefined;
        this.result = undefined;
        this.modalContent = undefined;
        this.headers = undefined;
        this.loaded = false;
        this.start = undefined;
        this.innerStyle = undefined;
        this.audioFile = undefined;
        this.title = undefined;
        this.description = undefined;
        this.types = {};
    }
    optionsUpdate(newValue, oldValue) {
        var _a, _b;
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['optionsUpdate'], newValue, oldValue);
        let opts = newValue;
        if (!!newValue && typeof newValue === 'string') {
            opts = JSON.parse(newValue);
        }
        if (!isEqual(opts, this.options)) {
            this.options = Object.assign({}, opts);
            (_b = this.LOG) === null || _b === void 0 ? void 0 : _b.debug(['optionsUpdate'], { options: this.options, newValue, oldValue });
        }
    }
    dataUpdate(newValue, oldValue) {
        var _a;
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['dataUpdate'], { newValue, oldValue });
        this.parseResult();
    }
    varsUpdate(newValue, oldValue) {
        var _a;
        let vars = this.vars;
        if (!!this.vars && typeof this.vars === 'string') {
            vars = JSON.parse(this.vars);
        }
        if (!isEqual(vars, this.innerVars)) {
            this.innerVars = vars;
            this.exec();
        }
        if (this.LOG) {
            (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['varsUpdate'], { vars: this.vars, newValue, oldValue });
        }
    }
    warpscriptUpdate(newValue, oldValue) {
        var _a;
        if (!!this.warpscript) {
            this.exec();
        }
        if (this.LOG) {
            (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['warpscriptUpdate'], {
                ws: this.ws,
                newValue, oldValue,
            });
        }
    }
    async discoveryEventHandler(event) {
        this.eventState = Utils.mergeDeep(this.eventState, Utils.parseEventData(event.detail, 'tag=.*,type=.*', this.componentId));
        const res = Utils.parseEventData(event.detail, this.options.eventHandler, this.componentId);
        if (res.popup && this.modal) {
            this.modalContent = res.popup;
            await this.modal.open();
        }
        if (res.style) {
            this.innerStyle = Object.assign(Object.assign({}, this.innerStyle), res.style);
        }
        if (res.audio) {
            this.audioFile = res.audio;
        }
        if (res.title) {
            this.title = res.title;
        }
        if (res.description) {
            this.description = res.description;
        }
        if (res.link) {
            if (res.link.target === '_blank') {
                window.open(res.link.link, '_blank').focus();
            }
            else {
                window.location.href = res.link.link;
            }
        }
        if (res.vars) {
            this.innerVars = Object.assign(Object.assign({}, (this.innerVars || {})), res.vars);
            if (!(this.options.mutedVars || []).includes(event.detail.selector)) {
                this.exec();
            }
        }
    }
    // noinspection JSUnusedGlobalSymbols
    componentWillLoad() {
        var _a;
        this.LOG = new Logger(DiscoveryDashboardComponent, this.debug);
        this.componentId = v4();
        if (!!this.options && typeof this.options === 'string' && this.options !== 'undefined') {
            this.options = JSON.parse(this.options || '{}');
        }
        if (this.options === 'undefined') {
            this.options = new Param();
        }
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['componentWillLoad'], { url: this.url, options: this.options });
        const dims = Utils.getContentBounds(this.el.parentElement);
        this.width = dims.w - 15;
        this.height = dims.h;
    }
    // noinspection JSUnusedGlobalSymbols
    componentDidLoad() {
        this.exec();
    }
    // noinspection JSUnusedGlobalSymbols
    disconnectedCallback() {
        var _a;
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['disconnectedCallback'], 'disconnected');
        if (this.timer) {
            window.clearInterval(this.timer);
        }
    }
    async getPDF(save = true, output = 'blob') {
        var _a, _b;
        try {
            const win = Utils.getContentBounds(this.dash);
            const struct = await this.getDashboardStructure();
            (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['getPDF'], struct);
            return await PdfLib.generatePDF(win.w, win.h, struct, save, output, this.LOG);
        }
        catch (e) {
            (_b = this.LOG) === null || _b === void 0 ? void 0 : _b.error(['getPDF'], e);
        }
    }
    async getVars() {
        var _a;
        return Promise.resolve(Object.assign(Object.assign({}, this.innerVars), (((_a = this.eventState) === null || _a === void 0 ? void 0 : _a.vars) || {})));
    }
    async getDashboardStructure() {
        var _a, _b;
        const result = Utils.clone(this.result);
        const tiles = Utils.clone(this.tiles);
        const res = await Promise.all(tiles.map((t => { var _a; return (_a = t.elem) === null || _a === void 0 ? void 0 : _a.export('png'); })));
        for (let i = 0; i < tiles.length; i++) {
            tiles[i].png = (_a = res[i]) === null || _a === void 0 ? void 0 : _a.dataUrl;
            tiles[i].bgColor = Utils.getCSSColor(this.el, '--warp-view-tile-background', (_b = res[i]) === null || _b === void 0 ? void 0 : _b.bgColor);
            tiles[i].uid = uuidv4();
            delete tiles[i].macro;
            delete tiles[i].data;
            delete tiles[i].elem;
            delete tiles[i].endpoint;
        }
        result.tiles = tiles.filter(t => t.type !== 'hidden');
        result.cellHeight = result.cellHeight || this.cellHeight || 220;
        result.cols = result.cols || this.cols || 12;
        result.bgColor = Utils.getCSSColor(this.el, '--warp-view-dashboard-background', '#fff');
        result.fontColor = Utils.getCSSColor(this.el, '--warp-view-font-color', '#000');
        return Object.assign(Object.assign({}, new Dashboard()), result);
    }
    exec() {
        this.ws = this.warpscript || Utils.unsescape(this.el.innerHTML);
        if (this.ws && this.ws !== '') {
            this.loaded = false;
            this.done = {};
            Utils.httpPost(Utils.getUrl(this.url), this.ws + ' DUP TYPEOF \'MACRO\' == <% EVAL %> IFT', this.options.httpHeaders)
                .then((res) => {
                var _a, _b;
                const result = new JsonLib().parse(res.data);
                const tmpResult = result.length > 0 ? result[0] : new Dashboard();
                this.options = Object.assign(Object.assign({}, this.options), tmpResult.options);
                this.headers = res.headers;
                this.headers.statusText = `Your script execution took ${GTSLib.formatElapsedTime(res.status.elapsed)} serverside, fetched ${res.status.fetched} datapoints and performed ${res.status.ops}  WarpLib operations.`;
                this.statusHeaders.emit(this.headers);
                this.loaded = true;
                this.start = new Date().getTime();
                if (this.autoRefresh !== this.options.autoRefresh) {
                    this.autoRefresh = this.options.autoRefresh;
                    if (this.timer) {
                        window.clearInterval(this.timer);
                    }
                    if (this.autoRefresh && this.autoRefresh > 0) {
                        this.timer = window.setInterval(() => this.exec(), this.autoRefresh * 1000);
                    }
                }
                this.innerType = tmpResult.type || this.type || 'dashboard';
                if (typeof tmpResult.tiles === 'string') {
                    (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['exec', 'macroTiles'], tmpResult.tiles);
                    const ws = LangUtils.prepare(Utils.unsescape(tmpResult.tiles + ' EVAL'), this.innerVars || {}, ((_b = (this.options)) === null || _b === void 0 ? void 0 : _b.skippedVars) || [], 'dashboard', 'warpscript');
                    Utils.httpPost(this.url, ws, this.options.httpHeaders).then((t) => {
                        var _a;
                        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['exec', 'macroTiles', 'res'], t);
                        this.renderedTiles = new JsonLib().parse(t.data)[0] || [];
                        this.processResult(tmpResult);
                    }).catch(e => {
                        var _a;
                        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.error(['exec'], e);
                        tmpResult.tiles = [];
                        this.processResult(tmpResult);
                    });
                }
                else {
                    this.renderedTiles = tmpResult.tiles || [];
                    this.processResult(tmpResult);
                }
            }).catch(e => {
                var _a;
                this.statusError.emit(e);
                (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.error(['exec'], e);
            });
        }
        else {
            this.parseResult();
        }
    }
    parseResult() {
        var _a;
        let tmpResult;
        if (!!this.data && typeof this.data === 'string') {
            const res = JSON.parse(this.data);
            tmpResult = GTSLib.isArray(res) ? res[0] : res;
        }
        else {
            tmpResult = GTSLib.isArray(this.data) ? this.data[0] : this.data;
        }
        this.options = Object.assign(Object.assign({}, this.options), tmpResult.options);
        this.innerType = tmpResult.type || this.type || 'dashboard';
        this.loaded = true;
        if (typeof tmpResult.tiles === 'string') {
            (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['exec', 'macroTiles'], tmpResult.tiles);
            this.processMacroTiles(tmpResult);
        }
        else {
            this.renderedTiles = tmpResult.tiles || [];
        }
        this.processResult(tmpResult);
    }
    processMacroTiles(tmpResult) {
        if (this.refreshTimer) {
            clearInterval(this.refreshTimer);
        }
        if (typeof tmpResult.tiles === 'string') {
            Utils.httpPost(this.url, tmpResult.tiles + ' EVAL', this.options.httpHeaders).then((t) => {
                var _a;
                (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['exec', 'macroTiles', 'res'], t);
                this.renderedTiles = new JsonLib().parse(t.data)[0] || [];
                this.processResult(tmpResult);
                if (this.options.autoRefresh || 0 > 0 && !!this.data) {
                    this.refreshTimer = setTimeout(() => this.processMacroTiles(tmpResult), this.options.autoRefresh * 1000);
                }
            }).catch(e => {
                var _a;
                (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.error(['exec'], e);
                tmpResult.tiles = [];
            });
        }
    }
    processResult(tmpResult) {
        var _a;
        if (!!this.vars && typeof this.vars === 'string') {
            this.innerVars = JSON.parse(this.vars);
        }
        else {
            this.innerVars = this.vars || {};
        }
        if (this.innerType === 'scada') {
            const tiles = tmpResult.tiles; // items array
            if (tiles.length > 0) {
                let y = 0;
                let height = 0;
                tiles.forEach(item => {
                    if (item.y >= y) {
                        y = item.y;
                        height = Math.max(y + item.h, height);
                    }
                });
                this.scadaHeight = height + 20;
            }
        }
        if (GTSLib.isArray(tmpResult.tiles)) {
            tmpResult.tiles = [...tmpResult.tiles || []];
        }
        else {
            tmpResult.tiles = tmpResult.tiles || [];
        }
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['processResult', 'tmpResult'], tmpResult);
        tmpResult.vars = Object.assign(Object.assign({}, tmpResult.vars || {}), this.innerVars);
        tmpResult.cols = tmpResult.cols || this.cols || 12;
        this.result = Object.assign({}, tmpResult);
        this.title = this.dashboardTitle || this.result.title;
        this.description = this.result.description;
        this.tiles = [];
        for (let i = 0; i < Object.assign({ tiles: {} }, this.result).tiles.length; i++) {
            this.done[i] = 0;
        }
    }
    static merge(options, options2) {
        if (typeof options === 'string') {
            options = JSON.parse(options);
        }
        let opts = Object.assign({}, options);
        return Object.assign(Object.assign(Object.assign(Object.assign({}, new Param()), opts), { eventHandler: undefined }), options2);
    }
    static mergeVars(vars) {
        let myVars = {};
        vars.map((v) => typeof v === 'string' ? JSON.parse(v) : v).forEach(v => myVars = Object.assign(Object.assign({}, myVars), v));
        return myVars;
    }
    // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
    static sanitize(data) {
        if (typeof data === 'string')
            return '["' + data + '"]';
        else
            return GTSLib.isArray(data) ? data : [data];
    }
    getType(id, type) {
        var _a, _b;
        return (this.types[id] || type || '').replace(/:/gi, '-') + (!!((_b = (_a = this.renderedTiles[id]) === null || _a === void 0 ? void 0 : _a.options) === null || _b === void 0 ? void 0 : _b.responsive) ? ' auto-height' : '');
    }
    getRowSpan(id, span) {
        var _a, _b;
        return !!((_b = (_a = this.renderedTiles[id]) === null || _a === void 0 ? void 0 : _a.options) === null || _b === void 0 ? void 0 : _b.responsive) ? `span ${span - 1}` : `${span}`;
    }
    setActualType(id, type) {
        type.stopPropagation();
        this.types[id] = type.detail;
        this.types = Object.assign({}, this.types);
    }
    getRendering() {
        var _a;
        switch (this.innerType) {
            case 'scada':
                return h("div", { class: "discovery-scada-main" }, h("h1", null, this.title), h("p", null, this.description), h("div", { class: "discovery-scada-wrapper", style: { height: `${this.scadaHeight}px` } }, (this.renderedTiles || []).map((t, i) => h("div", { class: 'discovery-scada-tile ' + this.getType(i, t.type), style: {
                        left: `${t.x}px`,
                        width: `${t.w}px`,
                        height: `${t.h}px`,
                        top: `${t.y}px`,
                        zIndex: `${(t.z || 0)}`,
                    } }, h("div", null, t.macro
                    ? h("discovery-tile", { url: t.endpoint || this.url, type: t.type, "chart-title": t.title, unit: t.unit, onSelfType: type => this.setActualType(i, type), debug: this.debug, id: `chart-${i}}`, ref: (el) => this.addTile(el, t, i), vars: JSON.stringify(DiscoveryDashboardComponent.mergeVars([this.result.vars, t.vars])), options: JSON.stringify(DiscoveryDashboardComponent.merge(this.options, t.options)) }, t.macro + ' EVAL')
                    : h("discovery-tile-result", { url: t.endpoint || this.url, result: DiscoveryDashboardComponent.sanitize(t.data), type: t.type, onSelfType: type => this.setActualType(i, type), ref: (el) => this.addTile(el, t, i), id: `chart-${i}}`, unit: t.unit, options: DiscoveryDashboardComponent.merge(this.options, t.options), debug: this.debug, "chart-title": t.title }))))));
            case 'dashboard':
                return this.result ?
                    h("div", { class: "discovery-dashboard-main" }, h("h1", null, this.title), h("p", null, this.description), h("div", { class: "discovery-dashboard-wrapper", style: {
                            width: '100%',
                            gridAutoRows: `minmax(${(((_a = this.result) === null || _a === void 0 ? void 0 : _a.cellHeight) || this.cellHeight)}px, auto)`,
                            gridTemplateColumns: `repeat(${this.result.cols}, 1fr)`,
                        } }, (this.renderedTiles || []).map((t, i) => h("div", { class: 'discovery-dashboard-tile ' + this.getType(i, t.type), style: {
                            gridColumn: `${(t.x + 1)} / ${(t.x + t.w + 1)}`,
                            gridRow: `${(t.y + 1)} / ${(t.y + t.h + 1)}`,
                            height: `${((this.result.cellHeight || this.cellHeight) * t.h + 10 * (t.h - 1) + 5)}px`,
                            minHeight: '100%',
                            gridRowEnd: this.getRowSpan(i, t.y + t.h + 1),
                        } }, h("div", null, t.macro
                        ? h("discovery-tile", { url: t.endpoint || this.url, type: t.type, "chart-title": t.title, debug: this.debug, unit: t.unit, id: `chart-${i}}`, ref: (el) => this.addTile(el, t, i), onSelfType: type => this.setActualType(i, type), vars: JSON.stringify(DiscoveryDashboardComponent.mergeVars([this.innerVars, this.result.vars, t.vars])), options: JSON.stringify(DiscoveryDashboardComponent.merge(this.options, t.options)) }, t.macro + ' EVAL')
                        : h("discovery-tile-result", { url: t.endpoint || this.url, result: DiscoveryDashboardComponent.sanitize(t.data), type: t.type, ref: (el) => this.addTile(el, t, i), unit: t.unit, id: `chart-${i}}`, onSelfType: type => this.setActualType(i, type), options: DiscoveryDashboardComponent.merge(this.options, t.options), debug: this.debug, "chart-title": t.title })))))) : '';
            case 'flex':
                return this.result ?
                    h("div", { class: "discovery-flex-main" }, h("h1", null, this.title), h("p", null, this.description), h("div", { class: "discovery-flex-wrapper" }, (this.renderedTiles || []).map((t, i) => h("div", { class: 'discovery-dashboard-tile ' + this.getType(i, t.type), style: {
                            height: `${((this.result.cellHeight || this.cellHeight) * t.h + 10 * (t.h - 1) + 5)}px`,
                            minHeight: '100%',
                            maxWidth: `calc(100% / ${this.result.cols} * ${t.w} - var(--warp-view-dashboard-gap, 10px) * 2)`,
                        } }, h("div", null, t.macro
                        ? h("discovery-tile", { url: t.endpoint || this.url, type: t.type, "chart-title": t.title, debug: this.debug, unit: t.unit, id: `chart-${i}}`, onSelfType: type => this.setActualType(i, type), ref: (el) => this.addTile(el, t, i), vars: JSON.stringify(DiscoveryDashboardComponent.mergeVars([this.result.vars, t.vars])), options: JSON.stringify(DiscoveryDashboardComponent.merge(this.options, t.options)) }, t.macro + ' EVAL')
                        : h("discovery-tile-result", { url: t.endpoint || this.url, result: DiscoveryDashboardComponent.sanitize(t.data), type: t.type, ref: (el) => this.addTile(el, t, i), unit: t.unit, onSelfType: type => this.setActualType(i, type), id: `chart-${i}}`, options: DiscoveryDashboardComponent.merge(this.options, t.options), debug: this.debug, "chart-title": t.title })))))) : '';
            default:
                return '';
        }
    }
    render() {
        return h(Host, { key: '20b2de232f8acc20bfb6e9d85daba9300b53dbc7' }, h("discovery-modal", { key: 'a1687f8ccd75d65eff2841352a8b8a6289a1e564', ref: (el) => this.modal = el, data: this.modalContent, options: this.options, url: this.url, debug: this.debug }), this.loaded
            ? [h("style", null, this.generateStyle(this.innerStyle)), h("div", { ref: el => this.dash = el }, this.getRendering()), this.audioFile ?
                    h("audio", { src: this.audioFile, autoPlay: true, id: "song" }) : '']
            : h("discovery-spinner", null, "Requesting data..."), h("pre", { key: '21e9e9725e1305017e503d6f51f03cb82c90aaa3', id: "ws" }, h("slot", { key: '2f36922adc8cf4da5ed9451d1fc4a0b5a3491367' })));
    }
    generateStyle(styles) {
        this.innerStyles = Object.assign(Object.assign(Object.assign({}, this.innerStyles), styles), this.options.customStyles || {});
        return Object.keys(this.innerStyles || {}).map(k => `${k} { ${this.innerStyles[k]} }`).join('\n');
    }
    addTile(el, t, i) {
        if (!!el) {
            t.elem = el;
            this.tiles.push(t);
            el.addEventListener('draw', () => {
                // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                this.done[i] = (this.done[i] || 0) + 1;
                const res = Object.keys(this.done).map(s => {
                    switch (this.tiles[i].type) {
                        case 'line':
                        case 'area':
                        case 'scatter':
                        case 'spline-area':
                        case 'step-area':
                        case 'spline':
                        case 'step':
                        case 'step-after':
                        case 'step-before':
                            return this.done[s] === !!this.tiles[i].macro ? 2 : 1;
                        default:
                            return this.done[s] === 1;
                    }
                });
                if ((this.renderedTiles || []).length === Object.keys(this.done).length && res.every(r => !!r)) {
                    this.rendered.emit();
                }
            });
        }
    }
    static get is() { return "discovery-dashboard"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["discovery-dashboard.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["discovery-dashboard.css"]
        };
    }
    static get properties() {
        return {
            "url": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "url",
                "reflect": false
            },
            "dashboardTitle": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "dashboard-title",
                "reflect": false
            },
            "data": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "Dashboard | string",
                    "resolved": "Dashboard | string",
                    "references": {
                        "Dashboard": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::Dashboard"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "data",
                "reflect": false
            },
            "warpscript": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "warpscript",
                "reflect": false
            },
            "options": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "Param | string",
                    "resolved": "Param | string",
                    "references": {
                        "Param": {
                            "location": "import",
                            "path": "../../model/param",
                            "id": "src/model/param.ts::Param"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "options",
                "reflect": false,
                "defaultValue": "new Param()"
            },
            "debug": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "debug",
                "reflect": false,
                "defaultValue": "false"
            },
            "autoRefresh": {
                "type": "number",
                "mutable": true,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "auto-refresh",
                "reflect": false,
                "defaultValue": "-1"
            },
            "cellHeight": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "cell-height",
                "reflect": false,
                "defaultValue": "220"
            },
            "cols": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "cols",
                "reflect": false,
                "defaultValue": "12"
            },
            "type": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "'scada' | 'dashboard' | 'flex'",
                    "resolved": "\"dashboard\" | \"flex\" | \"scada\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "type",
                "reflect": false,
                "defaultValue": "'dashboard'"
            },
            "vars": {
                "type": "any",
                "mutable": false,
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "vars",
                "reflect": false,
                "defaultValue": "'{}'"
            }
        };
    }
    static get states() {
        return {
            "width": {},
            "height": {},
            "result": {},
            "modalContent": {},
            "headers": {},
            "loaded": {},
            "start": {},
            "innerStyle": {},
            "audioFile": {},
            "title": {},
            "description": {},
            "types": {}
        };
    }
    static get events() {
        return [{
                "method": "statusHeaders",
                "name": "statusHeaders",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "string[]",
                    "resolved": "string[]",
                    "references": {}
                }
            }, {
                "method": "statusError",
                "name": "statusError",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "rendered",
                "name": "rendered",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "void",
                    "resolved": "void",
                    "references": {}
                }
            }];
    }
    static get methods() {
        return {
            "getPDF": {
                "complexType": {
                    "signature": "(save?: boolean, output?: string) => Promise<any>",
                    "parameters": [{
                            "name": "save",
                            "type": "boolean",
                            "docs": ""
                        }, {
                            "name": "output",
                            "type": "string",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<any>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "getVars": {
                "complexType": {
                    "signature": "() => Promise<any>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<any>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "getDashboardStructure": {
                "complexType": {
                    "signature": "() => Promise<Dashboard>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "Dashboard": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::Dashboard"
                        }
                    },
                    "return": "Promise<Dashboard>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "options",
                "methodName": "optionsUpdate"
            }, {
                "propName": "data",
                "methodName": "dataUpdate"
            }, {
                "propName": "vars",
                "methodName": "varsUpdate"
            }, {
                "propName": "warpscript",
                "methodName": "warpscriptUpdate"
            }];
    }
    static get listeners() {
        return [{
                "name": "discoveryEvent",
                "method": "discoveryEventHandler",
                "target": "window",
                "capture": false,
                "passive": false
            }];
    }
}
//# sourceMappingURL=discovery-dashboard.js.map
